import React from 'react';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, ProjectionVariant } from 'src/api/types';
import { DataTitle, DataValue, InfoIcon, NoData, Poppins, Spacer, VariantValues } from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import ExpandSection from 'src/components/ExpandSection';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { formatDate, numeralFormat } from 'src/utils/misc';
import styled from 'styled-components';
import { genFrequencyLabel } from '../util';
import { ScenarioQuery } from 'src/graphql/_generated';
import { LABELS } from 'src/components/labels';
import { isNil } from 'lodash';
import FormulaPreview, { isPlainNumberFormula } from 'src/components/FormulaPreview';
import { formulaReferencesUnpopulatedVariable } from 'src/components/FormulaInput/shared/formula';
import useVariables from 'src/hooks/useVariables';
import { useScenario } from 'src/state/scenario';

const Div = styled.div`
  .note-text {
    word-break: break-word;
  }

  .content {
    display: flex;
    justify-content: space-between;
  }

  /* FREQUENCY and CONSEQUENCE are two side-by-side, thin-bordered cards. */
  .notes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 24px;
    align-items: stretch;
  }

  .card {
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.white};
    box-sizing: border-box;
  }

  /* Each major block is a padded section. Full-bleed dividers between stacked
     sections (lower↔upper, upper↔note) are drawn automatically by the adjacent
     border-top — so they can never lead/trail/double, regardless of which
     sections render. The single-section frequency card gets no such border. */
  .card__section {
    padding: 16px;
  }
  .card__section + .card__section {
    border-top: 1px solid ${({ theme }) => theme.colors.stroke};
  }

  /* Stacked base/live/complete frequency rows inside the left card. */
  .freq-rows {
    display: flex;
    flex-direction: column;
    grid-gap: 20px;
    align-items: flex-start;
  }

  /* Inset split WITHIN a section (value↔formula, frequency↔note) — keeps the
     side-padding gaps, unlike the full-bleed section borders above. */
  .card__inset {
    height: 1px;
    background: ${({ theme }) => theme.colors.stroke};
    margin: 16px 0;
  }

  /* Inline formula pills (replaces the old bordered FormulaBox). */
  .formula-inline {
    margin-top: 10px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .risk-statement {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-gap: 10px;
    }
    .notes {
      grid-template-columns: 1fr;
      grid-gap: 16px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .risk-statement {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

// Read-only notice shown under a consequence when its formula references a
// variable with no value in this workspace (so the amount can't be calculated).
// Reuses the shared InfoIcon; matches the red "unpopulated" chip treatment.
const MissingVariablesBox = styled.div`
  display: flex;
  align-items: flex-start;
  grid-gap: 10px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.dropZoneErrBg};
  border: 1px solid ${({ theme }) => theme.colors.dropZoneErrBorder};

  .edit-link {
    color: ${({ theme }) => theme.colors.prussianBlue};
    text-decoration: underline;
    cursor: pointer;
  }
`;

const MissingVariablesAlert: React.FC<{ onEdit: () => void }> = ({ onEdit }) => (
  <MissingVariablesBox>
    <InfoIcon color="statusRed" cursorDefault style={{ minWidth: 20 }} />
    <Poppins px={14} color="prussianBlue">
      Missing required variable.{' '}
      <span className="edit-link" onClick={onEdit}>
        Edit this scenario
      </span>{' '}
      to add a value.
    </Poppins>
  </MissingVariablesBox>
);

interface RiskStatementSectionProps {
  scenario: NonNullable<ScenarioQuery['scenario']>;
  showAllProjected: boolean;
}

export const RiskStatementSection: React.FC<RiskStatementSectionProps> = ({ scenario, showAllProjected }) => {
  // Variables for this scenario's assessment — used to resolve the consequence
  // formula tokens (#id → name/icon/value) in the read-only display.
  const { variables } = useVariables({ assessmentId: scenario.assessment.id });
  const { openModal } = useScenario();

  // Opens the edit modal and scrolls it to the consequence formula inputs.
  const openScenarioAtFormula = () => openModal(scenario.id, { scrollToFormula: true });

  // A consequence whose formula references an unpopulated variable can't be
  // valued — show the missing-variables banner (and skip the value/inset divider).
  const lowerMissingVars = !!variables && formulaReferencesUnpopulatedVariable(scenario.lowerFormula, variables);
  const upperMissingVars = !!variables && formulaReferencesUnpopulatedVariable(scenario.upperFormula, variables);
  const showLowerFormula = !!scenario.lowerFormula && !isPlainNumberFormula(scenario.lowerFormula);
  const showUpperFormula = !!scenario.upperFormula && !isPlainNumberFormula(scenario.upperFormula);

  return (
    <ExpandSection title="RISK STATEMENT" sectionId="scenario_rs">
      <Div>
        <div className="content risk-statement">
          <div>
            <DataTitle>SOURCES</DataTitle>
            {!isNil(scenario.source) ? (
              <DataValue data-cy="scenario-source">{getRiskSourcesName(scenario.source)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>EVENTS</DataTitle>
            {!isNil(scenario.event) ? (
              <DataValue data-cy="scenario-event">{getRiskEventsName(scenario.event)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>CONSEQUENCES</DataTitle>
            {!isNil(scenario.consequence) ? (
              <DataValue data-cy="scenario-consequence">{getRiskConsequencesName(scenario.consequence)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>IDENTIFIED DATE</DataTitle>
            {scenario.riskIdentifiedDate ? (
              <DataValue data-cy="scenario-risk_identified_date">
                <TLLink date={scenario.riskIdentifiedDate}>{formatDate(scenario.riskIdentifiedDate)}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <Spacer $px={30} />
        <DataTitle>RISK NARRATIVE</DataTitle>
        <div />
        {scenario.riskNarrative ? <ExpandCollapse text={scenario.riskNarrative} /> : <NoData />}
        <Spacer $px={30} />
        <div className="notes">
          {/* ───────── FREQUENCY card — a single section (freq + inset-split note) ───────── */}
          <div className="card">
            <div className="card__section">
              <DataTitle>FREQUENCY</DataTitle>
              <Spacer $px={10} />

              {scenario.liveRisk.lowerThanMinimum?.includes('frequency') ? (
                <DataValue data-cy="scenario-frequency">Frequency lower than 1 time every 100 years</DataValue>
              ) : showAllProjected ? (
                <div className="freq-rows">
                  {genFrequencyLabel({
                    scenario: {
                      frequency_times: scenario.baseRisk?.frequencyTimes,
                      frequency_years: scenario.baseRisk?.frequencyYears,
                    },
                    tooltipText: 'Frequency without control modifiers applied',
                    variant: ProjectionVariant.base,
                    showChancePercent: true,
                  })}
                  {genFrequencyLabel({
                    scenario: {
                      frequency_times: scenario.liveRisk?.frequencyTimes,
                      frequency_years: scenario.liveRisk?.frequencyYears,
                    },
                    tooltipText: 'Frequency with control modifiers from live controls applied',
                    variant: ProjectionVariant.live,
                    showChancePercent: true,
                  })}
                  {genFrequencyLabel({
                    scenario: {
                      frequency_times: scenario.projectedRisk?.frequencyTimes,
                      frequency_years: scenario.projectedRisk?.frequencyYears,
                    },
                    tooltipText: 'Frequency with control modifiers from all controls applied',
                    variant: ProjectionVariant.complete,
                    showChancePercent: true,
                  })}
                </div>
              ) : (
                genFrequencyLabel({
                  scenario: {
                    frequency_times: scenario.liveRisk?.frequencyTimes,
                    frequency_years: scenario.liveRisk?.frequencyYears,
                  },
                  showChancePercent: true,
                })
              )}

              <div className="card__inset" />

              <DataTitle>NOTE</DataTitle>
              {scenario.frequencyNote ? (
                <ExpandCollapse preserveLineBreaks text={scenario.frequencyNote} dataCy="scenario-frequency-note" />
              ) : (
                <NoData />
              )}
            </div>
          </div>

          {/* ───────── CONSEQUENCE card — three sections (lower / upper / note) ───────── */}
          <div className="card">
            <div className="card__section">
              <DataTitle>LOWER CONSEQUENCE</DataTitle>
              <Spacer $px={10} />
              {lowerMissingVars ? (
                <NoData techIncomplete />
              ) : scenario.liveRisk.lowerThanMinimum?.includes('lower') ? (
                <DataValue data-cy="scenario-lower">{'<1'}</DataValue>
              ) : showAllProjected ? (
                <VariantValues
                  inline
                  value={{
                    base: scenario.baseRisk?.lower,
                    live: scenario.liveRisk?.lower,
                    cpmplete: scenario.projectedRisk?.lower,
                  }}
                  tooltipText={{
                    base: LABELS.modifierBaseConsequence,
                    live: LABELS.modifierConsequenceImpact,
                    cpmplete: LABELS.modifierCumulativeImpact,
                  }}
                />
              ) : scenario.liveRisk.lower === null ? (
                <div style={{ marginLeft: '5px' }}>
                  <NoData techIncomplete />
                </div>
              ) : (
                <DataValue data-cy="scenario-lower">{numeralFormat(scenario.liveRisk.lower)}</DataValue>
              )}
              {lowerMissingVars && <MissingVariablesAlert onEdit={openScenarioAtFormula} />}
              {showLowerFormula ? (
                <>
                  {lowerMissingVars ? <Spacer $px={16} /> : <div className="card__inset" />}
                  <DataTitle>LOWER CONSEQUENCE FORMULA</DataTitle>
                  <div className="formula-inline">
                    <FormulaPreview value={scenario.lowerFormula} variables={variables ?? undefined} showValueOnHover />
                  </div>
                </>
              ) : null}
            </div>

            <div className="card__section">
              <DataTitle>UPPER CONSEQUENCE</DataTitle>
              <Spacer $px={10} />
              {upperMissingVars ? (
                <NoData techIncomplete />
              ) : scenario.liveRisk.lowerThanMinimum?.includes('upper') ? (
                <DataValue data-cy="scenario-upper">{'<1'}</DataValue>
              ) : showAllProjected ? (
                <VariantValues
                  inline
                  value={{
                    base: scenario.baseRisk?.upper,
                    live: scenario.liveRisk?.upper,
                    cpmplete: scenario.projectedRisk?.upper,
                  }}
                  tooltipText={{
                    base: 'Consequence without control modifiers applied',
                    live: 'Consequence with control modifiers from live controls applied',
                    cpmplete: 'Consequence with control modifiers from all controls applied',
                  }}
                />
              ) : scenario.liveRisk.upper === null ? (
                <div style={{ marginLeft: '5px' }}>
                  <NoData techIncomplete />
                </div>
              ) : (
                <DataValue data-cy="scenario-upper">{numeralFormat(scenario.liveRisk.upper)}</DataValue>
              )}
              {upperMissingVars && <MissingVariablesAlert onEdit={openScenarioAtFormula} />}
              {showUpperFormula ? (
                <>
                  {upperMissingVars ? <Spacer $px={16} /> : <div className="card__inset" />}
                  <DataTitle>UPPER CONSEQUENCE FORMULA</DataTitle>
                  <div className="formula-inline">
                    <FormulaPreview value={scenario.upperFormula} variables={variables ?? undefined} showValueOnHover />
                  </div>
                </>
              ) : null}
            </div>

            <div className="card__section">
              <DataTitle>NOTE</DataTitle>
              {scenario.consequenceNote ? (
                <ExpandCollapse preserveLineBreaks text={scenario.consequenceNote} dataCy="scenario-sonsequence-note" />
              ) : (
                <NoData />
              )}
            </div>
          </div>
        </div>
      </Div>
    </ExpandSection>
  );
};
